import { DataFieldUsage } from 'src/app/services/api.services';
import { VisualFieldAllowed, VisualFieldNotAllowedReason } from './CanAddVisualFieldError';
import { Visual } from './Visual';
import { VisualField } from './VisualField';
import { VisualFieldRole, VisualFieldRoleInput } from './VisualFieldRole';
import { VisualFieldRoleTypeOrKind, VisualFieldRoleType } from './VisualFieldRoleType';
import { VisualFieldTarget } from './VisualFieldTarget';

function usagePriorityForSort(r: VisualFieldRole) {
  return r.usagePriority ?? (r.allowsMeasures ? -1 : -2);
}

export class VisualFieldRoleCollection implements Iterable<VisualFieldRole> {
  constructor(public readonly visual: Visual) { }

  readonly value: ReadonlyArray<VisualFieldRole> =
    this.visual.type.fieldRoleMap.types
      .map(r => new VisualFieldRole(this.visual, r))
      .sort((a, b) => usagePriorityForSort(b) - usagePriorityForSort(a));

  get size() { return this.value.length; }

  find(typeOrKind: VisualFieldRoleTypeOrKind | string): VisualFieldRole {
    let type =
      (typeOrKind instanceof VisualFieldRoleType)
        ? typeOrKind
        : this.visual.type.fieldRoleMap.find(typeOrKind);

    if (!type)
      return null;

    let role = this.value.find(r => r.type === type);
    if (!role)
      return null;

    return role;
  }

  get(roleInput: VisualFieldRoleInput): VisualFieldRole {
    let role =
      (roleInput instanceof VisualFieldRole)
        ? roleInput
        : this.find(roleInput);

    if (!role)
      throw new Error(`Invalid role`, { cause: roleInput });

    return role;
  }

  *[Symbol.iterator](): Iterator<VisualFieldRole> {
    for (let r of this.value)
      yield r;
  }

  /**
   * Returns the default field role that the field should be added to.
   * Returns null, if the field should not be added to any role. */
  forField(field: VisualField | VisualFieldTarget, ignoreLimits?: boolean) {
    let defaultUsage =
      field instanceof VisualField
        ? field.target.defaultUsage
        : field.defaultUsage;

    let kind =
      defaultUsage === DataFieldUsage.Measure
        ? VisualFieldRoleType.Kind.Values :
      defaultUsage === DataFieldUsage.Detail
        ? VisualFieldRoleType.Kind.Detail 
        : VisualFieldRoleType.Kind.Grouping;

    let roles = [...this.visual.fieldRoles.value]
      .sort((a, b) => (a.kind === kind ? 0 : 1)
                    - (b.kind === kind ? 0 : 1));

    return roles.find(r => {
      let fieldAllowed = r.fieldAllowed(field);
      if (fieldAllowed == VisualFieldAllowed)
        return true;

      if (fieldAllowed == VisualFieldNotAllowedReason.LimitReached && ignoreLimits)
        return true;

      return false;
    });
  }
}
